import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Brandmark, Wordmark } from "@/components/Brand";
import { toast } from "@/hooks/use-toast";

/**
 * /forgot-password — request a password-reset email.
 *
 * After submit we do NOT tell the user whether the email exists (username
 * enumeration is a real abuse vector), we just say "if that email is
 * registered, check your inbox".
 *
 * A 30s client-side cooldown prevents accidental double-clicks from
 * burning our Supabase rate-limit budget. The server enforces its own
 * 1-per-60s limit as the real defense.
 */
const COOLDOWN_SECONDS = 30;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (cooldown > 0) return;
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    setSubmitting(false);
    if (error) {
      // We still show the generic success message to avoid leaking which
      // emails are registered. Log silently for observability.
      console.warn("resetPasswordForEmail error", error);
    }
    setSent(true);
    setCooldown(COOLDOWN_SECONDS);
    toast({
      title: "Check your inbox",
      description: "If that email is registered, we've sent reset instructions.",
    });
  }

  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto flex min-h-screen max-w-[1080px] flex-col items-center justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <Brandmark size={32} />
          <Wordmark className="text-[24px]" />
        </div>

        <div className="w-full max-w-[420px] rounded-xl border border-line bg-canvas p-8 shadow-[0_1px_2px_rgba(26,25,21,.04)]">
          <h1 className="font-serif text-[28px] leading-[1.1] text-ink">
            Forgot your <em className="italic text-accent-rf">password</em>?
          </h1>
          <p className="mt-2 text-[13px] text-ink-3">
            Enter the email associated with your Routeflow account. If we find it, you'll get a reset link.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-3">
            <label className="block">
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-ink-3">Email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="varun@brand.in"
                className="auth-input"
                autoComplete="email"
              />
            </label>

            <button
              type="submit"
              disabled={submitting || cooldown > 0}
              className="mt-2 w-full rounded-md bg-ink px-4 py-2.5 text-[13px] font-medium text-canvas transition-colors duration-[120ms] hover:bg-ink/90 disabled:opacity-50"
            >
              {submitting
                ? "Sending…"
                : cooldown > 0
                ? `Resend available in ${cooldown}s`
                : sent
                ? "Resend link"
                : "Send reset link"}
            </button>
          </form>

          {sent && (
            <div className="mt-4 rounded-md border border-line bg-canvas-2 p-3 text-[12.5px] text-ink-2">
              If that email is registered, instructions are on the way. The link expires in 1 hour.
            </div>
          )}

          <div className="mt-6 text-center text-[12px] text-ink-3">
            Remembered it?{" "}
            <Link to="/auth" className="font-medium text-ink underline-offset-2 hover:underline">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          border-radius: 6px;
          border: 1px solid hsl(var(--line));
          background: hsl(var(--canvas));
          padding: 9px 12px;
          font-size: 13px;
          color: hsl(var(--ink));
          transition: border-color 120ms;
        }
        .auth-input::placeholder { color: hsl(var(--ink-3)); }
        .auth-input:focus { outline: none; border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}
